import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../icons';
import { apiV2 } from '../../lib/api';
import { navigateGlobal, buildPath } from '../../lib/route';
import { AppLink, isNewTabClick } from '../../lib/openLink';
import { getThemeColors } from '../../lib/theme';

/** Treffer von GET /v2/admin/search — ein Profil (BK oder PB-Familienmitglied). */
export interface SearchHit {
  type: 'bk' | 'pb';
  user_id: number;
  profile_id: number;
  family_account_id: number | null;
  name: string;
  email: string | null;
  phone: string | null;
  ort: string | null;
  is_pflegeempfaenger: boolean;
  beendet: boolean;
}

export async function searchRecords(q: string, signal?: AbortSignal): Promise<SearchHit[]> {
  const res = await apiV2.get<{ data: SearchHit[] }>('/admin/search', { params: { q, limit: 20 }, signal });
  return res.data.data ?? [];
}

/** Pfad des Profils (für echte Links / Neuer-Tab), null wenn nicht öffenbar. */
export function searchHitPath(hit: SearchHit): string | null {
  if (hit.type === 'bk') return buildPath('betreuung', String(hit.user_id));
  if (hit.family_account_id != null) return buildPath('pflege', String(hit.user_id));
  return null;
}

/** Öffnet das Profil: BK → /betreuung/{user}, PB → /pflege/{userId} (users.id führt seit 23.09.2026). */
export function openSearchHit(hit: SearchHit): void {
  if (hit.type === 'bk') {
    navigateGlobal('betreuung', String(hit.user_id));
  } else if (hit.family_account_id != null) {
    navigateGlobal('pflege', String(hit.user_id));
  }
}

const TYPE_STYLE = {
  bk: { labelKey: 'call_popup.badge_bk', bg: '#ecfdf3', fg: '#067647', border: '#abefc6' },
  pb: { labelKey: 'call_popup.badge_pb', bg: '#eff8ff', fg: '#175cd3', border: '#b2ddff' },
} as const;

/**
 * Navbar-Suche über alle BK/PB-Datensätze. Tippen → Dropdown mit Typ-Badge,
 * Klick/Enter öffnet das Profil. Strg/⌘+K fokussiert, Esc schließt.
 */
export function GlobalSearch({ dark }: { dark: boolean }) {
  const t = getThemeColors(dark);
  const { t: i18nT } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(0);

  // Strg/⌘+K → Suche fokussieren.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Außerhalb klicken → schließen (Header nutzt dasselbe document-Muster).
  useEffect(() => {
    const onClick = () => setOpen(false);
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  // Debounce + Abbruch veralteter Requests.
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2 && !/^\d+$/.test(term)) {
      setHits([]); setLoading(false); setError(false);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const res = await searchRecords(term, ctrl.signal);
        setHits(res); setActive(0); setError(false);
      } catch (e) {
        if ((e as { name?: string })?.name !== 'CanceledError') setError(true);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => { ctrl.abort(); window.clearTimeout(timer); };
  }, [q]);

  const choose = (hit: SearchHit) => {
    openSearchHit(hit);
    setOpen(false);
    setQ('');
    inputRef.current?.blur();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); return; }
    if (!hits.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(i => (i + 1) % hits.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => (i - 1 + hits.length) % hits.length); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(hits[active]); }
  };

  const term = q.trim();
  const showDropdown = open && (term.length >= 2 || /^\d+$/.test(term));
  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
      <span style={{ position: 'absolute', left: 11, display: 'flex' }}>
        <Icon.Search c={t.placeholder} size={15}/>
      </span>
      <input
        ref={inputRef}
        value={q}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={i18nT('header.search.placeholder')}
        aria-label={i18nT('header.search.aria_label')}
        role="combobox"
        aria-expanded={showDropdown}
        style={{
          fontSize: 13, border: `1px solid ${t.border}`, borderRadius: 8,
          padding: '8px 40px 8px 34px', width: 260, color: t.text,
          background: t.inputBg, fontFamily: 'Outfit, sans-serif',
          outline: 'none',
        }}
      />
      <span style={{
        position: 'absolute', right: 8,
        background: t.cardMuted, border: `1px solid ${t.border}`,
        borderRadius: 5, padding: '1px 6px', fontSize: 10, color: t.muted,
      }}>
        {isMac ? '⌘ K' : i18nT('header.search.shortcut_ctrl')}
      </span>

      {showDropdown && (
        <div className="fade-in" role="listbox" style={{
          position: 'absolute', left: 0, top: 44, width: 380,
          background: t.card, border: `1px solid ${t.border}`,
          borderRadius: 12, boxShadow: '0 12px 24px -8px rgba(16,24,40,0.18)',
          padding: 6, zIndex: 1000, maxHeight: 420, overflowY: 'auto',
        }}>
          {loading && hits.length === 0 && (
            <div style={{ fontSize: 12, color: t.muted, padding: '10px 8px' }}>{i18nT('header.search.searching')}</div>
          )}
          {error && (
            <div style={{ fontSize: 12, color: '#f04438', padding: '10px 8px' }}>{i18nT('header.search.failed')}</div>
          )}
          {!loading && !error && hits.length === 0 && (
            <div style={{ fontSize: 12, color: t.muted, padding: '10px 8px', fontStyle: 'italic' }}>
              {i18nT('header.search.no_results', { term })}
            </div>
          )}
          {hits.map((h, i) => {
            const ts = TYPE_STYLE[h.type];
            const sub = [h.email, h.phone, h.ort].filter(Boolean).join(' · ');
            const disabled = h.type === 'pb' && h.family_account_id == null;
            const hitPath = searchHitPath(h);
            const RowTag: any = disabled || !hitPath ? 'div' : AppLink;
            const linkProps = disabled || !hitPath ? {} : { to: hitPath, onClick: (e: React.MouseEvent) => {
              // Strg/Cmd/Mittelklick: Browser öffnet neuen Tab, Suche bleibt unberührt.
              if (isNewTabClick(e)) return;
              e.preventDefault(); choose(h);
            } };
            return (
              <RowTag
                key={`${h.type}-${h.profile_id}`}
                role="option"
                aria-selected={i === active}
                onMouseEnter={() => setActive(i)}
                {...linkProps}
                title={disabled ? i18nT('header.search.no_family_account') : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 8px', borderRadius: 8, textDecoration: 'none', color: 'inherit',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.55 : 1,
                  background: i === active ? t.hover : 'transparent',
                }}
              >
                <span style={{
                  fontSize: 10.5, fontWeight: 700, letterSpacing: '0.03em',
                  padding: '2px 7px', borderRadius: 6, flexShrink: 0,
                  background: dark ? 'transparent' : ts.bg, color: ts.fg,
                  border: `1px solid ${ts.border}`, minWidth: 30, textAlign: 'center',
                }}>
                  {i18nT(ts.labelKey)}
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: t.heading, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {h.name}
                    <span style={{ fontWeight: 400, color: t.muted, fontSize: 11.5 }}>
                      {' '}· {h.type === 'bk' ? i18nT('betreuung.sidebar.role') : h.is_pflegeempfaenger ? i18nT('header.search.type_pb') : i18nT('header.search.type_angehoerige')}
                      {h.beendet ? ` · ${i18nT('header.search.ended')}` : ''}
                    </span>
                  </div>
                  {sub && (
                    <div style={{ fontSize: 11.5, color: t.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }}>
                      {sub}
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 11, color: t.placeholder, flexShrink: 0 }}>{h.type === 'bk' ? i18nT('call_popup.badge_bk') : i18nT('call_popup.badge_pb')}-{h.user_id}</span>
              </RowTag>
            );
          })}
        </div>
      )}
    </div>
  );
}
